import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Upload, X, Eye, Image, Plus, Images, Code, Type, CalendarClock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { ImageGalleryModal } from '../../components/ImageGalleryModal';
import { MarkdownHelpModal } from '../../components/MarkdownHelpModal';
import { InsertDiagramMenu } from '../../components/InsertDiagramMenu';
import { WysiwygEditor, computeImageStyle } from '../../components/WysiwygEditor';
import { DraftPreview } from '../../components/DraftPreview';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PostEditor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const contentInputRef = useRef(null);
  const contentImageInputRef = useRef(null);
  const editorRef = useRef(null);
  const isEditing = !!slug;

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    featured_image: '',
    category: '',
    categories: [],
    tags: [],
    published: false,
    publish_at: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [categoryInput, setCategoryInput] = useState('');
  const [editorMode, setEditorMode] = useState('wysiwyg'); // 'wysiwyg' or 'markdown'
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [pendingImageUrl, setPendingImageUrl] = useState('');
  const [imageSize, setImageSize] = useState('medium');
  const [imageAlign, setImageAlign] = useState('center');

  useEffect(() => {
    if (isEditing) {
      fetchPost();
    }
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/posts/${slug}`, { withCredentials: true });
      setFormData(response.data);
    } catch (error) {
      toast.error('Failed to load post');
      navigate('/admin/posts');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-generate slug from title
    if (name === 'title' && !isEditing) {
      const generatedSlug = value
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-');
      setFormData(prev => ({ ...prev, slug: generatedSlug }));
    }
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!formData.tags.includes(tagInput.trim())) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, tagInput.trim()]
        }));
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleAddCategory = (e) => {
    if (e.key === 'Enter' && categoryInput.trim()) {
      e.preventDefault();
      const cats = formData.categories || [];
      if (!cats.includes(categoryInput.trim())) {
        setFormData(prev => ({
          ...prev,
          categories: [...(prev.categories || []), categoryInput.trim()],
          category: prev.categories?.length ? prev.category : categoryInput.trim()
        }));
      }
      setCategoryInput('');
    }
  };

  const handleRemoveCategory = (catToRemove) => {
    setFormData(prev => {
      const updated = (prev.categories || []).filter(c => c !== catToRemove);
      return {
        ...prev,
        categories: updated,
        category: updated[0] || ''
      };
    });
  };

  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, featured_image: response.data.url }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleContentImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPendingImageUrl(response.data.url);
      setImageDialogOpen(true);
      toast.success('Image uploaded - now set size and position');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleGallerySelect = (imageData) => {
    if (editorMode === 'wysiwyg' && editorRef.current) {
      editorRef.current.insertImage(imageData.url, imageData.size, imageData.align);
    } else {
      const imageTag = `\n\n[IMG:${imageData.url}|size:${imageData.size}|align:${imageData.align}]\n\n`;
      const textarea = contentInputRef.current;
      const cursorPos = textarea?.selectionStart || formData.content.length;
      const newContent = 
        formData.content.slice(0, cursorPos) + 
        imageTag + 
        formData.content.slice(cursorPos);
      setFormData(prev => ({ ...prev, content: newContent }));
    }
  };

  const insertDiagram = (tpl) => {
    if (editorMode === 'wysiwyg' && editorRef.current?.insertMermaid) {
      editorRef.current.insertMermaid(tpl.code);
    } else {
      const block = `\n\n\`\`\`mermaid\n${tpl.code}\n\`\`\`\n\n`;
      const textarea = contentInputRef.current;
      const cursorPos = textarea?.selectionStart ?? formData.content.length;
      const newContent =
        formData.content.slice(0, cursorPos) + block + formData.content.slice(cursorPos);
      setFormData(prev => ({ ...prev, content: newContent }));
      setTimeout(() => textarea?.focus(), 0);
    }
    toast.success(`${tpl.label} diagram inserted`);
  };

  const insertImageIntoContent = () => {
    if (!pendingImageUrl) return;

    const sizeMap = {
      small: '25%',
      medium: '50%',
      large: '75%',
      full: '100%'
    };

    const fullUrl = pendingImageUrl.startsWith('/api') 
      ? `${process.env.REACT_APP_BACKEND_URL}${pendingImageUrl}` 
      : pendingImageUrl;

    const size = sizeMap[imageSize];

    if (editorMode === 'wysiwyg' && editorRef.current) {
      editorRef.current.insertImage(fullUrl, size, imageAlign);
    } else {
      const imageTag = `\n\n[IMG:${fullUrl}|size:${size}|align:${imageAlign}]\n\n`;
      const textarea = contentInputRef.current;
      const cursorPos = textarea?.selectionStart || formData.content.length;
      const newContent = 
        formData.content.slice(0, cursorPos) + 
        imageTag + 
        formData.content.slice(cursorPos);
      setFormData(prev => ({ ...prev, content: newContent }));
    }

    setImageDialogOpen(false);
    setPendingImageUrl('');
    setImageSize('medium');
    setImageAlign('center');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.slug || !formData.content) {
      toast.error('Please fill in title, slug, and content');
      return;
    }

    // Clean HTML before saving: strip <p> wrappers from inside <li>
    const cleanContent = formData.content.replace(/<li><p>([\s\S]*?)<\/p><\/li>/gi, '<li>$1</li>');
    const saveData = { ...formData, content: cleanContent };

    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/posts/${slug}`, saveData, { withCredentials: true });
        toast.success('Post updated');
      } else {
        await axios.post(`${API}/posts`, saveData, { withCredentials: true });
        toast.success('Post created');
      }
      navigate('/admin/posts');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-64 bg-muted rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl" data-testid="post-editor">
      <div className="mb-8">
        <Link 
          to="/admin/posts"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to posts
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">
          {isEditing ? 'Edit Post' : 'New Post'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="Post title"
              data-testid="post-title-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">Slug *</Label>
            <Input
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              required
              placeholder="post-url-slug"
              data-testid="post-slug-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="excerpt">Excerpt</Label>
          <Textarea
            id="excerpt"
            name="excerpt"
            value={formData.excerpt}
            onChange={handleChange}
            placeholder="Brief summary of the post..."
            rows={2}
            data-testid="post-excerpt-input"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label htmlFor="content">Content *</Label>
              {editorMode === 'markdown' && <MarkdownHelpModal />}
              <InsertDiagramMenu onInsert={insertDiagram} />
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowPreview(true)}
                data-testid="preview-btn"
              >
                <Eye className="mr-2 h-4 w-4" />
                Preview
              </Button>

              <div className="flex border border-input rounded-md overflow-hidden">
                <button
                  type="button"
                  onClick={() => setEditorMode('wysiwyg')}
                  className={`px-2 py-1 text-xs flex items-center gap-1 transition-colors ${
                    editorMode === 'wysiwyg'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:bg-muted'
                  }`}
                  data-testid="mode-wysiwyg-btn"
                >
                  <Type className="h-3 w-3" />
                  Visual
                </button>
                <button
                  type="button"
                  onClick={() => setEditorMode('markdown')}
                  className={`px-2 py-1 text-xs flex items-center gap-1 transition-colors ${
                    editorMode === 'markdown'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:bg-muted'
                  }`}
                  data-testid="mode-markdown-btn"
                >
                  <Code className="h-3 w-3" />
                  Markdown
                </button>
              </div>

              <input
                type="file"
                ref={contentImageInputRef}
                onChange={handleContentImageUpload}
                accept="image/*"
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setGalleryOpen(true)}
                data-testid="gallery-btn"
              >
                <Images className="mr-2 h-4 w-4" />
                Gallery
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => contentImageInputRef.current?.click()}
                disabled={uploading}
                data-testid="insert-image-btn"
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload
              </Button>
            </div>
          </div>

          {editorMode === 'wysiwyg' ? (
            <WysiwygEditor
              ref={editorRef}
              content={formData.content}
              onChange={(html) => setFormData(prev => ({ ...prev, content: html }))}
              placeholder="Start writing your post..."
            />
          ) : (
            <>
              <Textarea
                ref={contentInputRef}
                id="content"
                name="content"
                value={formData.content}
                onChange={handleChange}
                required
                placeholder="Write your post content here..."
                rows={15}
                className="font-mono text-sm"
                data-testid="post-content-input"
              />
              <p className="text-xs text-muted-foreground">
                Markdown mode — paste or write Markdown (headings, tables, lists, images, diagrams). Raw HTML also works. Switch to Visual for rich-text editing.
              </p>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="category">Categories</Label>
            <Input
              id="category"
              value={categoryInput}
              onChange={(e) => setCategoryInput(e.target.value)}
              onKeyDown={handleAddCategory}
              placeholder="Press Enter to add categories"
              data-testid="post-category-input"
            />
            {formData.categories?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.categories.map((cat) => (
                  <span 
                    key={cat}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded text-sm"
                  >
                    {cat}
                    <button 
                      type="button"
                      onClick={() => handleRemoveCategory(cat)}
                      className="hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="tags">Tags</Label>
            <Input
              id="tags"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleAddTag}
              placeholder="Press Enter to add tags"
              data-testid="post-tags-input"
            />
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.tags.map((tag) => (
                  <span 
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded text-sm"
                  >
                    {tag}
                    <button 
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Featured Image</Label>
          <div className="flex items-center gap-4">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFeaturedImageUpload}
              accept="image/*"
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="upload-image-btn"
            >
              <Upload className="mr-2 h-4 w-4" />
              {uploading ? 'Uploading...' : 'Upload Image'}
            </Button>
            {formData.featured_image && (
              <div className="flex items-center gap-2">
                <img
                  src={formData.featured_image.startsWith('/api')
                    ? `${process.env.REACT_APP_BACKEND_URL}${formData.featured_image}`
                    : formData.featured_image}
                  alt="Preview"
                  className="h-12 w-12 object-cover rounded"
                />
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, featured_image: '' }))}
                  className="text-destructive hover:text-destructive/80"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
          <Input
            name="featured_image"
            value={formData.featured_image}
            onChange={handleChange}
            placeholder="Or enter image URL..."
            className="mt-2"
            data-testid="post-image-url-input"
          />
        </div>

        <div className="flex items-center gap-3 p-4 border border-border rounded-lg">
          <Switch
            id="published"
            checked={formData.published}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, published: checked }))}
            data-testid="post-published-switch"
          />
          <Label htmlFor="published" className="cursor-pointer">
            <span className="font-medium">Published</span>
            <p className="text-sm text-muted-foreground">
              {formData.published ? 'This post is visible to the public' : 'This post is saved as a draft'}
            </p>
          </Label>
        </div>

        {formData.published && (
          <div className="space-y-2">
            <Label htmlFor="publish_at">Schedule (optional)</Label>
            <Input
              id="publish_at"
              name="publish_at"
              type="datetime-local"
              value={formData.publish_at ? formData.publish_at.slice(0, 16) : ''}
              onChange={(e) => {
                const val = e.target.value;
                setFormData(prev => ({
                  ...prev,
                  publish_at: val ? new Date(val).toISOString() : ''
                }));
              }}
              data-testid="post-publish-at"
            />
            <p className="text-xs text-muted-foreground">
              {formData.publish_at
                ? `Scheduled for ${new Date(formData.publish_at).toLocaleString()}. Post won't be visible until then.`
                : 'Leave empty to publish immediately.'}
            </p>
          </div>
        )}

        <div className="flex items-center gap-4 pt-4">
          <Button type="submit" disabled={saving} data-testid="save-post-btn">
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : isEditing ? 'Update Post' : 'Create Post'}
          </Button>
          {isEditing && formData.published && (
            <Link to={`/post/${slug}`} target="_blank">
              <Button type="button" variant="outline">
                <Eye className="mr-2 h-4 w-4" />
                View Post
              </Button>
            </Link>
          )}
        </div>
      </form>

      {/* Image Insert Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insert Image</DialogTitle>
            <DialogDescription>
              Choose the size and alignment for your image
            </DialogDescription>
          </DialogHeader>
          
          {pendingImageUrl && (
            <div className="my-4">
              <img
                src={pendingImageUrl.startsWith('/api')
                  ? `${process.env.REACT_APP_BACKEND_URL}${pendingImageUrl}`
                  : pendingImageUrl}
                alt="Preview"
                className="max-h-48 mx-auto rounded"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Size</Label>
              <Select value={imageSize} onValueChange={setImageSize}>
                <SelectTrigger data-testid="image-size-select">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small (25%)</SelectItem>
                  <SelectItem value="medium">Medium (50%)</SelectItem>
                  <SelectItem value="large">Large (75%)</SelectItem>
                  <SelectItem value="full">Full Width (100%)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Alignment</Label>
              <Select value={imageAlign} onValueChange={setImageAlign}>
                <SelectTrigger data-testid="image-align-select">
                  <SelectValue placeholder="Select alignment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImageDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={insertImageIntoContent} data-testid="confirm-insert-image">
              <Plus className="mr-2 h-4 w-4" />
              Insert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Gallery Modal */}
      <ImageGalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        onSelect={handleGallerySelect}
      />

      {/* Draft Preview */}
      {showPreview && (
        <DraftPreview
          post={formData}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}
